/**
 * Single source of truth for unit conversions (length + area).
 *
 * Every conversion in the app should come from here so the factors and the
 * rounding behaviour stay consistent. All functions round to `precision`
 * decimals and return a number (not a string).
 */

export const FEET_PER_METER = 3.28084;
export const SQ_FEET_PER_SQ_METER = 10.7639;
export const INCHES_PER_FOOT = 12;
export const INCHES_PER_DECIMETER = 3.937;
export const METERS_PER_INCH = 0.0254;
export const DECIMETERS_PER_METER = 10;

export type LengthUnit = 'm' | 'm2';

function round(val: number, precision: number) {
	return +val.toFixed(precision);
}

/* ------------------------------------------------------------------ */
/* meters <-> feet (handles both length `m` and area `m2`)             */
/* ------------------------------------------------------------------ */

export function convertMetricToFeet(val: number, unit: LengthUnit = 'm', precision = 2) {
	if (unit === 'm2') return round(val * SQ_FEET_PER_SQ_METER, precision);
	return round(val * FEET_PER_METER, precision);
}

export function convertFeetToMetric(val: number, unit: LengthUnit = 'm', precision = 2) {
	if (unit === 'm2') return round(val / SQ_FEET_PER_SQ_METER, precision);
	return round(val / FEET_PER_METER, precision);
}

export const metersToFeet = (m: number, precision = 2) => convertMetricToFeet(m, 'm', precision);
export const feetToMeters = (ft: number, precision = 2) => convertFeetToMetric(ft, 'm', precision);
export const sqMetersToSqFeet = (m2: number, precision = 2) => convertMetricToFeet(m2, 'm2', precision);
export const sqFeetToSqMeters = (ft2: number, precision = 2) => convertFeetToMetric(ft2, 'm2', precision);

/* ------------------------------------------------------------------ */
/* meters <-> inches                                                    */
/* ------------------------------------------------------------------ */

export function metersToInches(m: number, precision = 2) {
	return round(m / METERS_PER_INCH, precision);
}

export function inchesToMeters(inches: number, precision = 4) {
	return round(inches * METERS_PER_INCH, precision);
}

/* ------------------------------------------------------------------ */
/* feet <-> inches                                                      */
/* ------------------------------------------------------------------ */

export function feetToInches(ft: number, precision = 2) {
	return round(ft * INCHES_PER_FOOT, precision);
}

export function inchesToFeet(inches: number, precision = 2) {
	return round(inches / INCHES_PER_FOOT, precision);
}

/* ------------------------------------------------------------------ */
/* decimeters <-> inches / meters                                       */
/* ------------------------------------------------------------------ */

export function decimetersToInches(dm: number, precision = 3) {
	return round(dm * INCHES_PER_DECIMETER, precision);
}

export function inchesToDecimeters(inches: number, precision = 3) {
	return round(inches / INCHES_PER_DECIMETER, precision);
}

export function metersToDecimeters(m: number, precision = 3) {
	return round(m * DECIMETERS_PER_METER, precision);
}

export function decimetersToMeters(dm: number, precision = 3) {
	return round(dm / DECIMETERS_PER_METER, precision);
}

export function decimetersToFeet(dm: number, precision = 2) {
	return metersToFeet(dm / DECIMETERS_PER_METER, precision);
}

export function feetToDecimeters(ft: number, precision = 3) {
	return round(feetToMeters(ft, 6) * DECIMETERS_PER_METER, precision);
}

/* ------------------------------------------------------------------ */
/* display helpers                                                      */
/* ------------------------------------------------------------------ */

/** Formats meters as `12' 6"`. */
export function convertMetricToLinearFeet(valInMeters: number) {
	const totalFeet = valInMeters * FEET_PER_METER;

	let feet = Math.floor(totalFeet);
	let inches = Math.round((totalFeet - feet) * INCHES_PER_FOOT);

	// inches can round up to a full foot
	if (inches >= INCHES_PER_FOOT) {
		feet += 1;
		inches = 0;
	}

	return `${feet}' ${inches}"`;
}
